import pclnExperimentation from '@pcln/experimentation'
import type { LocationSearchType } from '@pcln/horizon-search-form'
import { formatPackageSearchHref } from '@pcln/horizon-search-form'
import { Box, Link, Text } from 'pcln-design-system'
import { useEffect, useRef } from 'react'

import useSeti from '@/hooks/useSeti'

import {
  fireAdvancedPackageSearchLinkClick,
  trackCustomPackageSearchPromotionClick,
  trackCustomPackageSearchPromotionView,
  trackPackageSearchLinkClick,
  trackPackageSearchLinkView,
} from '../ga4'
import type { MakeWatchObject, PackagesFormStateType } from '../types'
import { isHotelPlusCar } from './TripTypes'

type AdvancedSearchLinkProps = Readonly<{
  pageName: 'homepage' | 'landing'
  watchState: MakeWatchObject<PackagesFormStateType>
}>

function AdvancedSearchLinkFlight({ pageName, watchState }: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay,
  } = watchState
  const isSearchMultipleDatesVariant =
    useSeti({
      experimentCode: 'PKG_ADVANCED_SEARCH_COPY_UPDATE_PT2',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredCopyUpdateImpressionRef = useRef(false)
  useEffect(() => {
    trackPackageSearchLinkView(pageName, watchTripType)
  }, [pageName, watchTripType])
  useEffect(() => {
    if (!hasFiredCopyUpdateImpressionRef.current) {
      hasFiredCopyUpdateImpressionRef.current = true
      pclnExperimentation.fireImpression('PKG_ADVANCED_SEARCH_COPY_UPDATE_PT2')
      trackCustomPackageSearchPromotionView(pageName)
    }
  }, [pageName])
  return (
    <Box>
      <Link
        href={formatPackageSearchHref(
          pageName,
          watchTripType,
          watchStartLocation as LocationSearchType | null | undefined,
          watchEndLocation as LocationSearchType | null | undefined,
          watchStartDate,
          watchEndDate,
          watchTravelers,
          watchHotelStartDate,
          watchHotelEndDate,
          watchPartialStay,
        )}
        target='_blank'
        onClick={() => {
          trackPackageSearchLinkClick(pageName, watchTripType)
          trackCustomPackageSearchPromotionClick(pageName)
          fireAdvancedPackageSearchLinkClick(pageName)
        }}
      >
        <Text fontSize='14px' fontWeight='bold'>
          {isSearchMultipleDatesVariant
            ? 'Search multiple dates or destinations'
            : 'Custom Package Search (One-Way Flight, Multi-Hotel)'}
        </Text>
      </Link>
    </Box>
  )
}

function AdvancedSearchLinkHotelCar({ pageName, watchState }: AdvancedSearchLinkProps) {
  const {
    watchTripType,
    watchStartLocation,
    watchEndLocation,
    watchStartDate,
    watchEndDate,
    watchTravelers,
    watchHotelStartDate,
    watchHotelEndDate,
    watchPartialStay,
  } = watchState
  const advancedSearchHotelCarLinkSeti =
    useSeti({
      experimentCode: 'PKG_HP_ADVANCED_SEARCH_HC',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredPromotionViewRef = useRef(false)
  useEffect(() => {
    pclnExperimentation.fireImpression('PKG_HP_ADVANCED_SEARCH_HC')
  }, [])
  useEffect(() => {
    if (advancedSearchHotelCarLinkSeti) {
      trackPackageSearchLinkView(pageName, watchTripType)
    }
  }, [pageName, watchTripType, advancedSearchHotelCarLinkSeti])
  useEffect(() => {
    if (advancedSearchHotelCarLinkSeti && !hasFiredPromotionViewRef.current) {
      hasFiredPromotionViewRef.current = true
      trackCustomPackageSearchPromotionView(pageName)
    }
  }, [pageName, advancedSearchHotelCarLinkSeti])
  return (
    advancedSearchHotelCarLinkSeti && (
      <Box>
        <Link
          href={formatPackageSearchHref(
            pageName,
            watchTripType,
            watchStartLocation as LocationSearchType | null | undefined,
            watchEndLocation as LocationSearchType | null | undefined,
            watchStartDate,
            watchEndDate,
            watchTravelers,
            watchHotelStartDate,
            watchHotelEndDate,
            watchPartialStay,
          )}
          target='_blank'
          onClick={() => {
            trackPackageSearchLinkClick(pageName, watchTripType)
            trackCustomPackageSearchPromotionClick(pageName)
          }}
        >
          <Text fontSize='14px' fontWeight='bold'>
            Custom Package Search (Multi-Hotel, Custom Car Dates)
          </Text>
        </Link>
      </Box>
    )
  )
}

export default function AdvancedSearchLink({ pageName, watchState }: AdvancedSearchLinkProps) {
  const { watchTripType } = watchState
  const showAdvancedSearchLinkHotelCar = isHotelPlusCar(watchTripType)
  return (
    <>
      {watchTripType.includes('FLY') && (
        <AdvancedSearchLinkFlight pageName={pageName} watchState={watchState} />
      )}
      {showAdvancedSearchLinkHotelCar && (
        <AdvancedSearchLinkHotelCar pageName={pageName} watchState={watchState} />
      )}
    </>
  )
}
